import React from "react";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from "recharts";
import { Users, CalendarDays, Wallet, Receipt, Hourglass, AlertTriangle } from "lucide-react";
import { fmtEUR, fmtEUR2, fmtDate } from "@/lib/format";
import { CARD, Label } from "@/components/common";

export const KIND_LABEL = { secu: "Sécu / CMU", mutuelle: "Mutuelle", alma: "Alma" };
const KIND_STYLE = { secu: "bg-sky-50 text-sky-700", mutuelle: "bg-violet-50 text-violet-700", alma: "bg-amber-50 text-amber-700" };
const COLORS = { cb: "#065F46", especes: "#10B981", cheque: "#6EE7B7", secu: "#0EA5E9", mutuelle: "#8B5CF6", alma: "#F59E0B", autre: "#9CA3AF" };

function Kpi({ icon: Icon, label, value, sub, testid, tone = "text-gray-900" }) {
  return (
    <div className={`${CARD} p-4 sm:p-5`} data-testid={testid}>
      <div className="flex items-center gap-2 text-gray-400"><Icon className="h-4 w-4" /><Label>{label}</Label></div>
      <p className={`mt-1 font-display text-2xl font-black sm:text-3xl ${tone}`}>{value}</p>
      {sub && <p className="mt-1 text-xs text-gray-400">{sub}</p>}
    </div>
  );
}

export function MonthKpis({ data }) {
  const k = data.kpis;
  return (
    <div className="grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-4">
      <Kpi icon={Wallet} label="CA du mois" value={fmtEUR(k.total)} tone="text-emerald-700" testid="kpi-total"
        sub={`Immédiat ${fmtEUR(k.immediate)} · Différé ${fmtEUR(k.deferred)}`} />
      <Kpi icon={Users} label="Patients" value={k.patients} testid="kpi-patients" sub={`${k.unique_patients} patients distincts`} />
      <Kpi icon={Receipt} label="Panier moyen" value={fmtEUR2(k.avg_patient)} testid="kpi-avg-patient" sub="par passage patient" />
      <Kpi icon={CalendarDays} label="Journées" value={k.days} testid="kpi-days" sub={`Moyenne ${fmtEUR(k.avg_day)} / jour`} />
    </div>
  );
}

export function ModesCard({ modes, total }) {
  return (
    <div className={`${CARD} p-5 sm:p-6`} data-testid="modes-card">
      <Label className="mb-3">Répartition par mode de paiement</Label>
      {modes.length === 0 ? <p className="py-6 text-sm text-gray-400">Aucun encaissement ce mois-ci.</p> : (
        <div className="flex flex-col items-center gap-4 sm:flex-row">
          <div className="h-44 w-44 shrink-0">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={modes} dataKey="amount" nameKey="name" innerRadius={48} outerRadius={80} paddingAngle={2} stroke="none">
                  {modes.map((m) => <Cell key={m.key} fill={COLORS[m.key]} />)}
                </Pie>
                <Tooltip formatter={(v) => fmtEUR2(v)} />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="w-full space-y-2">
            {modes.map((m) => (
              <div key={m.key} className="flex items-center justify-between text-sm" data-testid={`mode-${m.key}`}>
                <span className="flex items-center gap-2"><span className="h-2.5 w-2.5 rounded-full" style={{ background: COLORS[m.key] }} />{m.name}</span>
                <span className="font-semibold">{fmtEUR2(m.amount)} <span className="text-xs font-normal text-gray-400">{total ? Math.round((m.amount / total) * 100) : 0} %</span></span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export function PendingCard({ data }) {
  return (
    <div className={`${CARD} p-5 sm:p-6`} data-testid="pending-card">
      <div className="flex items-center gap-2 text-gray-400"><Hourglass className="h-4 w-4" /><Label>En attente de règlement</Label></div>
      <p className="mt-1 font-display text-3xl font-black text-amber-600" data-testid="pending-total">{fmtEUR2(data.pending_total)}</p>
      <div className="mt-3 flex flex-wrap gap-2">
        {Object.entries(data.pending_by_kind).filter(([, v]) => v > 0).map(([k, v]) => (
          <span key={k} className={`rounded-full px-2.5 py-1 text-xs font-bold ${KIND_STYLE[k]}`} data-testid={`pending-kind-${k}`}>{KIND_LABEL[k]} · {fmtEUR2(v)}</span>
        ))}
      </div>
      {data.pending_late > 0 && (
        <p className="mt-3 flex items-center gap-1.5 text-xs font-semibold text-rose-600" data-testid="pending-late"><AlertTriangle className="h-3.5 w-3.5" />{fmtEUR2(data.pending_late)} en retard de virement</p>
      )}
      <p className="mt-2 text-xs text-gray-400">Virements reçus ce mois-ci : <b className="text-gray-600">{fmtEUR2(data.received_month)}</b></p>
      <div className="mt-4 max-h-72 divide-y divide-gray-50 overflow-y-auto">
        {data.pending.length === 0 ? <p className="py-3 text-sm text-gray-400">Tout est réglé.</p> : data.pending.map((p) => (
          <div key={p.id} className="flex items-center justify-between gap-3 py-2.5" data-testid={`pending-row-${p.id}`}>
            <div className="min-w-0">
              <p className="truncate text-sm font-medium">{p.patient || "—"}{p.mutuelle ? <span className="text-gray-400"> · {p.mutuelle}</span> : null}</p>
              <p className="text-xs text-gray-400"><span className={`mr-1 rounded px-1.5 py-0.5 font-semibold ${KIND_STYLE[p.kind]}`}>{KIND_LABEL[p.kind]}</span>
                prévu le {fmtDate(p.expected_date)}{p.late && <span className="ml-1 font-semibold text-rose-600">· en retard</span>}</p>
            </div>
            <span className="shrink-0 font-display font-bold">{fmtEUR2(p.amount)}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

export function RetroCard({ retro }) {
  if (!retro) return (
    <div className={`${CARD} p-5 sm:p-6`} data-testid="retro-card"><Label>Ma rétrocession estimée</Label>
      <p className="mt-2 text-sm text-gray-400">Aucun taux de rétrocession n'est défini sur votre fiche.</p></div>
  );
  const rows = [
    ["CA encaissé immédiat (CB, espèces, chèques…)", retro.ca_immediate],
    ["+ Virements Sécu / Mutuelle / Alma reçus", retro.ca_recu_differe],
    ["= CA retenu", retro.ca, true],
    [`× Taux de rétrocession ${retro.taux} %`, retro.brut],
    ["− Frais fixes et déductions", -retro.deductions],
    ...(retro.encaisse_direct ? [[retro.encaisse_direct_mode ? "− CB, espèces et chèques encaissés par vous" : "− CB encaissée sur votre TPE", -retro.encaisse_direct]] : []),
  ];
  return (
    <div className={`${CARD} p-5 sm:p-6`} data-testid="retro-card">
      <Label>Ma rétrocession estimée</Label>
      <p className={`mt-1 font-display text-3xl font-black ${retro.net < 0 ? "text-amber-700" : "text-emerald-700"}`} data-testid="retro-net">{fmtEUR2(Math.abs(retro.net))}</p>
      <p className="text-xs font-semibold text-gray-500" data-testid="retro-sens">{Math.abs(retro.net) < 0.005 ? "Rien à régler" : retro.net > 0 ? "À recevoir du cabinet" : "À reverser au cabinet"}</p>
      <div className="mt-4 space-y-2 text-sm">
        {rows.map(([l, v, b]) => (
          <div key={l} className={`flex justify-between gap-3 ${b ? "border-t border-gray-100 pt-2 font-semibold" : "text-gray-500"}`}>
            <span>{l}</span><span className="shrink-0">{fmtEUR2(v)}</span>
          </div>
        ))}
      </div>
      {retro.ca_en_attente > 0 && <p className="mt-3 text-xs text-amber-600">{fmtEUR2(retro.ca_en_attente)} prévus ce mois-ci pas encore reçus : ils seront comptés à réception.</p>}
    </div>
  );
}
